import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';
import { gyroBridge, setFromDeviceEvent, recentre } from '../state/gyroBridge';
import { ROOMS } from '../rooms';

// GyroLook — the phone becomes the head.
//
// Position stays with whatever is driving locomotion (the touch joystick, WASD);
// this owns ROTATION only. That split is the whole design: you walk with your
// thumb and look by turning your body, which is what makes a phone held up in
// front of you read as a window into the room rather than a screen.
//
// ── IT OWNS THE ROTATION OUTRIGHT WHILE IT IS ON ─────────────────────────────
// InteriorWalk and WalkControls both write camera.quaternion from their own
// drag/arrow state. Two writers per frame is the exact failure that produced the
// entry jitter and the seat inversion earlier — so they stand down (they check
// gyroBridge.active) rather than being overridden here. Whoever owns the camera
// owns it alone.
//
// ── PRIORITY MUST STAY 0 ─────────────────────────────────────────────────────
// In r3f, ANY useFrame with a priority above 0 switches off the automatic
// render — the contract is "if you want to order yourself, you now own drawing".
// This was written at priority 1 to sit after locomotion and would have shown a
// black screen the instant it mounted, gyro on or off, with nothing in the
// console. Ordering comes from mount order instead: this is mounted after the
// walk rigs in PlanetScene, so it runs after them anyway.
export default function GyroLook() {
  const camera = useThree((s) => s.camera);
  const gyro = useWorldStore((s) => s.gyro);
  const interior = useWorldStore((s) => s.interior);
  const smoothed = useRef(new THREE.Quaternion());
  const primed = useRef(false);

  // Recentre on entering a room: "forward" should mean the room's own spawn
  // heading, and a room you walk into is a new place to be facing.
  useEffect(() => { if (gyro) recentre(); }, [gyro, interior]);

  useEffect(() => {
    if (!gyro) { gyroBridge.active = false; return undefined; }
    // `deviceorientation` rather than `deviceorientationabsolute`: absolute is
    // Chrome-only and unavailable on iOS, and we re-base alpha ourselves anyway,
    // so true north buys nothing and costs the platform.
    const onOrient = (e) => { if (setFromDeviceEvent(e)) gyroBridge.active = true; };
    const onScreen = () => recentre();   // rotating the phone changes what forward means
    window.addEventListener('deviceorientation', onOrient, true);
    window.addEventListener('orientationchange', onScreen);
    screen.orientation?.addEventListener?.('change', onScreen);
    primed.current = false;
    return () => {
      window.removeEventListener('deviceorientation', onOrient, true);
      window.removeEventListener('orientationchange', onScreen);
      screen.orientation?.removeEventListener?.('change', onScreen);
      gyroBridge.active = false;
    };
  }, [gyro]);

  useFrame((_, dt) => {
    if (!gyro || !gyroBridge.active) return;

    // FACE THE ROOM, NOT NORTH. The bridge re-bases alpha so that whichever way
    // the phone is pointing when you switch on becomes zero; this rotates that
    // zero to the room's authored spawn heading, so switching gyro on inside the
    // salon leaves you looking at the salon rather than at whatever compass
    // bearing you happened to be holding.
    const look = ROOMS[interior]?.spawn?.look;
    const pos = ROOMS[interior]?.spawn?.pos;
    let base = 0;
    if (look && pos) base = Math.atan2(look[0] - pos[0], look[2] - pos[2]);
    const yaw = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), base);
    const target = yaw.multiply(gyroBridge.quat);

    // A LITTLE SMOOTHING, and no more. Phone gyros are noisy enough that raw
    // values shimmer when you hold still, and heavy smoothing turns head
    // movement into swimming — which is the thing that actually makes people
    // ill. Time-based so it behaves the same at 60 and at 120fps.
    if (!primed.current) { smoothed.current.copy(target); primed.current = true; }
    else smoothed.current.slerp(target, 1 - Math.exp(-18 * dt));
    camera.quaternion.copy(smoothed.current);
  });

  return null;
}
